import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { Card, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { TabButton, TabGroup } from '../../ui/Tabs/index.js';
import { bdCell, columnOf, labelOf } from './bd-defs.js';
import { splitFlowNodes, splitTestSteps, tally, tabToneOf, toneOf } from './bd-presentation.js';
import type { BdArtifactDef, BdRow } from '../../../lib/api/basic-design.js';

export interface ImpactListViewProps {
  /** 影響一覧の定義（列の見出し・方向・テスト種別・実施の表示名） */
  def: BdArtifactDef;
  /** 保存済みの行 */
  rows: readonly BdRow[];
}

/**
 * 影響一覧を読む。行を方向で色分けしたカードで並べ、方向ごとの件数で絞れるようにする。
 *
 * 8 つの欄を 1 行の表に並べると、何が起きる・テストのような長い文が細い列に押し込まれて
 * 読めない。1 行をカードにして、劣化・依存のように目を留めるべき行は色で拾えるようにする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 影響一覧を、方向で色分けしたカードと方向ごとの件数の絞り込みで読む
 * @graph-connects tally [calls] 方向ごとの件数
 * @graph-connects ImpactRowCard [calls] 1 行のカード
 */
export function ImpactListView({ def, rows }: ImpactListViewProps) {
  const { t } = useTranslation();
  // 空文字は絞らない（すべて）
  const [direction, setDirection] = useState('');
  const counts = tally(
    rows.map((r) => bdCell(r, 'direction')),
    columnOf(def, 'direction')?.choices,
    t('bd.filter.unselected'),
  );
  const shown = rows
    .map((row, index) => ({ row, index }))
    .filter(({ row }) => direction === '' || bdCell(row, 'direction') === direction);

  return (
    <div className="space-y-3">
      <TabGroup ariaLabel={t('bd.impact.filterAria')}>
        <TabButton
          active={direction === ''}
          label={t('bd.filter.all')}
          count={rows.length}
          onClick={() => {
            setDirection('');
          }}
        />
        {counts.map((c) => (
          <TabButton
            key={c.value}
            active={direction === c.value}
            label={c.label}
            count={c.count}
            tone={tabToneOf('direction', c.value)}
            disabled={c.count === 0}
            onClick={() => {
              setDirection(c.value);
            }}
          />
        ))}
      </TabGroup>

      <ol className="space-y-3" aria-label={t('bd.view.rowsAria', { title: def.title })}>
        {shown.map(({ row, index }) => (
          // 同じ中身の行が並ぶことがあるので、保存済みの並びの位置で鍵を作る
          <li key={index}>
            <ImpactRowCard def={def} row={row} number={index + 1} />
          </li>
        ))}
      </ol>
    </div>
  );
}

interface ImpactRowCardProps {
  def: BdArtifactDef;
  row: BdRow;
  /** 保存済みの並びでの行番号（1 から） */
  number: number;
}

/**
 * 影響一覧の 1 行のカード。見出しに方向・業務フロー・ノード、本文に何が起きる、
 * 下に根拠とテスト（Given / When / Then を 3 段で）を置く。
 * @graph-connects splitTestSteps [calls] テストの 3 段への分割
 * @graph-connects splitFlowNodes [calls] ノードの分割
 */
function ImpactRowCard({ def, row, number }: ImpactRowCardProps) {
  const nodes = splitFlowNodes(bdCell(row, 'flowNodes'));
  const test = bdCell(row, 'test');
  const steps = splitTestSteps(test);
  const label = (key: string): string => columnOf(def, key)?.label ?? key;
  const choice = (key: string): string => labelOf(columnOf(def, key), bdCell(row, key));

  return (
    <Card padding="sm">
      <CardContent className="mt-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs tabular-nums text-[var(--color-text-muted)]">#{number}</span>
          <Badge variant={toneOf('direction', bdCell(row, 'direction'))}>
            {choice('direction')}
          </Badge>
          <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
            {bdCell(row, 'businessFlow')}
          </h3>
          {nodes.length === 0 ? null : (
            <ul className="ml-auto flex flex-wrap gap-1" aria-label={label('flowNodes')}>
              {nodes.map((node, i) => (
                // 同じノードを 2 度書いた行もそのまま出すので、位置で鍵を作る
                <li key={`${String(i)}:${node}`}>
                  <code className="rounded bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-xs text-[var(--color-text-secondary)]">
                    {node}
                  </code>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-text-primary)]">
          {bdCell(row, 'whatHappens')}
        </p>

        <dl className="mt-3 grid grid-cols-1 gap-x-4 gap-y-2 border-t border-[var(--color-border)] pt-3 text-sm md:grid-cols-[5rem_1fr]">
          <dt className="text-xs text-[var(--color-text-muted)]">{label('evidence')}</dt>
          <dd className="break-all font-mono text-xs text-[var(--color-text-secondary)]">
            {bdCell(row, 'evidence')}
          </dd>

          <dt className="text-xs text-[var(--color-text-muted)]">{label('test')}</dt>
          <dd>
            <div className="flex flex-wrap gap-1">
              <Badge variant={toneOf('testType', bdCell(row, 'testType'))}>
                {choice('testType')}
              </Badge>
              <Badge>{choice('testOwner')}</Badge>
            </div>
            {steps === null ? (
              <p className="mt-1 whitespace-pre-wrap text-[var(--color-text-primary)]">{test}</p>
            ) : (
              <dl className="mt-1 grid grid-cols-[3.5rem_1fr] gap-x-2 gap-y-0.5">
                <dt className="text-xs font-semibold text-[var(--color-text-muted)]">Given</dt>
                <dd className="text-[var(--color-text-primary)]">{steps.given}</dd>
                <dt className="text-xs font-semibold text-[var(--color-text-muted)]">When</dt>
                <dd className="text-[var(--color-text-primary)]">{steps.when}</dd>
                <dt className="text-xs font-semibold text-[var(--color-text-muted)]">Then</dt>
                <dd className="text-[var(--color-text-primary)]">{steps.then}</dd>
              </dl>
            )}
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
}
